<template>
  <v-form
    ref="entityForm"
    class="entity-form"
    data-testid="entity-form"
  >
    <section class="entity-form__section" data-testid="entity-form__details">
      <h3 class="entity-form__heading">Entity details</h3>
      <div class="entity-form__grid">
        <div class="entity-form__field" data-testid="entity-form__name">
          <lb-string
            label="Name"
            v-model="modifydata['name']"
            :error="errors['name']"
            class="always-float-label"
          />
        </div>
        <div class="entity-form__field" data-testid="entity-form__type">
          <lb-dropdown
            label="Type"
            v-model="modifydata['type']"
            :error="errors['type']"
            :items="selectdata.type"
            class="always-float-label"
          />
        </div>
        <div class="entity-form__field" data-testid="entity-form__contact-person">
          <lb-string
            label="Contact Person"
            v-model="modifydata['contactperson']"
            :error="errors['contactperson']"
            class="always-float-label"
          />
        </div>
        <div class="entity-form__field" data-testid="entity-form__email">
          <lb-string
            label="Email"
            v-model="modifydata['contactemail']"
            :error="errors['contactemail']"
            class="always-float-label"
          />
        </div>
        <div class="entity-form__field" data-testid="entity-form__contact-no">
          <lb-number
            label="Contact No."
            v-model="modifydata['contactno']"
            :error="errors['contactno']"
            type="tel"
            class="always-float-label"
          />
        </div>
        <div class="entity-form__field" data-testid="entity-form__industry">
          <lb-dropdown
            v-model="modifydata['industry']"
            :items="industries"
            label="Industry*"
            :error="errors['industry']"
            class="always-float-label"
          />
        </div>
        <div class="entity-form__field" data-testid="entity-form__sector">
          <lb-dropdown
            v-model="modifydata['sector']"
            :items="sectors[modifydata['industry']]"
            label="Sector*"
            :error="errors['sector']"
            class="always-float-label"
          />
        </div>
        <div class="entity-form__field" data-testid="entity-form__size">
          <lb-dropdown
            v-model="modifydata['size']"
            :items="size"
            label="Size*"
            :error="errors['size']"
            class="always-float-label"
          />
        </div>
        <div class="entity-form__field" data-testid="entity-form__address">
          <lb-string
            label="Address"
            v-model="modifydata['address']"
            :error="errors['address']"
            class="always-float-label"
          />
        </div>
        <div class="entity-form__field" data-testid="entity-form__plan">
          <lb-dropdown
            label="Plan"
            v-model="modifydata['plan']"
            :error="errors['plan']"
            :items="plans"
            :multiple="false"
            class="always-float-label"
          />
        </div>
        <div class="entity-form__field" data-testid="entity-form__account-manager">
          <lb-dropdown
            label="Account Manager"
            v-model="modifydata['accountmanager']"
            :error="errors['accountmanager']"
            :items="allUsers"
            multiple
            itemtext="name"
            itemvalue="_id"
            class="always-float-label"
          />
        </div>
        <div class="entity-form__field" data-testid="entity-form__pin-count">
          <v-text-field
            v-model.number="modifydata.pinCount"
            label="Shortcut Pin Count"
            class="entity-form__pin always-float-label"
            dense
            outlined
            type="number"
            min="-1"
            max="10"
            hide-details="auto"
            :hint="modifydata.pinCount === -1 ? '-1 means disabled' : ''"
            :persistent-hint="modifydata.pinCount === -1"
            :rules="[pinCountRule]"
            data-testid="entity-form__pin-count-input"
            @keypress="onPinKeypress"
            @input="onPinInput"
          />
        </div>
      </div>
    </section>

    <section class="entity-form__section" data-testid="entity-form__features">
      <h3 class="entity-form__heading">Features</h3>
      <div class="entity-form__features">
        <div
          v-for="feature in features"
          :key="feature.key"
          class="entity-form__feature"
          :class="{ 'entity-form__feature--wide': feature.showLimit && modifydata[feature.key] }"
          :data-testid="`entity-form__feature--${feature.key}`"
        >
          <label
            :id="`entity-form-feature-${feature.key}`"
            class="entity-form__feature-label"
          >
            {{ feature.label }}
          </label>
          <div class="entity-form__feature-control">
            <v-switch
              :input-value="featureOn(feature)"
              :aria-labelledby="`entity-form-feature-${feature.key}`"
              class="entity-form__switch"
              color="success"
              hide-details
              inset
              dense
              :data-testid="`entity-form__${feature.key}-switch`"
              @change="onFeatureChange(feature, $event)"
            />
            <v-progress-circular
              v-if="feature.loading && isFeatureLoading(feature)"
              indeterminate
              color="success"
              size="20"
              width="2"
              :data-testid="`entity-form__${feature.key}-loading`"
            />
            <v-text-field
              v-if="feature.showLimit && modifydata[feature.key]"
              v-model.number="modifydata.aiLimit"
              label="AI Limit"
              class="entity-form__limit always-float-label"
              dense
              outlined
              type="number"
              hide-details="auto"
              :rules="[aiLimitRule]"
              required
              data-testid="entity-form__ai-limit"
            />
          </div>
        </div>
      </div>
    </section>

    <div class="entity-form__actions">
      <v-btn
        text
        class="secondaryBtn"
        data-testid="entity-form__cancel-btn"
        @click="$emit('cancel')"
      >
        Cancel
      </v-btn>
      <v-btn
        v-if="addflag"
        class="primaryBtn"
        elevation="0"
        data-testid="entity-form__save-btn"
        @click="handleSave"
      >
        Save
      </v-btn>
      <v-btn
        v-else-if="editflag"
        class="primaryBtn"
        elevation="0"
        data-testid="entity-form__update-btn"
        @click="handleUpdate"
      >
        Update
      </v-btn>
    </div>
  </v-form>
</template>

<script>
const FEATURES = [
  { key: 'aiEnabled', label: 'Gen AI', emit: 'ai-enabled', loading: 'aiEnabledLoading', showLimit: true },
  { key: 'user_permissions_superadmin', label: 'User Permission', emit: 'user-permission-enabled', loading: 'userPermissionLoading' },
  { key: 'schedulerEnabled', label: 'Notification' },
  { key: 'sod', label: 'SOD', local: true },
  { key: 'horizonViewEnabled', label: 'Horizon View' },
  { key: 'exportManagerEnabled', label: 'Export Manager' },
  { key: 'upsiEnabled', label: 'UPSI Controls' },
  { key: 'chatbotEnabled', label: 'Chatbot' },
  { key: 'accrualsbotEnabled', label: 'Accruals' }
];

const PIN_COUNT_MIN = -1;
const PIN_COUNT_MAX = 10;

export default {
  name: 'EntityForm',
  props: {
    addflag: { type: Boolean, default: false },
    editflag: { type: Boolean, default: false },
    modifydata: { type: Object, default: () => ({}) },
    errors: { type: Object, default: () => ({}) },
    selectdata: { type: Object, default: () => ({}) },
    industries: { type: Array, default: () => [] },
    sectors: { type: Object, default: () => ({}) },
    size: { type: Array, default: () => [] },
    plans: { type: Array, default: () => [] },
    allUsers: { type: Array, default: () => [] },
    isEnabled: { type: Boolean, default: false },
    aiEnabledLoading: { type: Boolean, default: false },
    userPermissionLoading: { type: Boolean, default: false }
  },
  data() {
    return {
      features: FEATURES,
      localIsEnabled: this.isEnabled
    };
  },
  created() {
    if (this.modifydata && this.modifydata.pinCount == null) {
      this.$set(this.modifydata, 'pinCount', PIN_COUNT_MIN);
    }
  },
  beforeDestroy() {
    this.localIsEnabled = false;
  },
  watch: {
    isEnabled(newVal) {
      this.localIsEnabled = newVal;
    }
  },
  methods: {
    featureOn(feature) {
      if (feature.local) return this.localIsEnabled;
      return !!this.modifydata[feature.key];
    },
    isFeatureLoading(feature) {
      return !!this[feature.loading];
    },
    onFeatureChange(feature, value) {
      if (feature.local) {
        this.localIsEnabled = value;
        return;
      }
      this.$set(this.modifydata, feature.key, value);
      if (feature.emit) this.$emit(feature.emit, this.modifydata);
    },
    aiLimitRule(value) {
      return !!value || 'AI Limit is required';
    },
    pinCountRule(value) {
      return (value >= PIN_COUNT_MIN && value <= PIN_COUNT_MAX) || 'Value must be -1 to 10';
    },
    onPinKeypress(event) {
      const minusAfterStart = event.key === '-' && event.target.value.length > 0;
      if (!/[\d-]/.test(event.key) || minusAfterStart) event.preventDefault();
    },
    onPinInput(value) {
      const parsed = parseInt(value, 10);
      if (!isNaN(parsed)) {
        this.modifydata.pinCount = Math.min(PIN_COUNT_MAX, Math.max(PIN_COUNT_MIN, parsed));
      }
    },
    handleSave() {
      if (!this.$refs.entityForm.validate()) return;
      this.$emit('save');
    },
    handleUpdate() {
      if (!this.$refs.entityForm.validate()) return;
      this.$emit('update', this.localIsEnabled);
    }
  }
};
</script>

<style lang="scss" scoped>
@import './EntityForm.scss';
</style>
